import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import useSound from "use-sound";
import Cookies from "js-cookie";
import "./EggTimer.css";
import beepSound from "../assets/beep.mp3";
import AdUnit from "./AdUnit";
import FAQ from "./FAQ";
import Navbar from "./Navbar";

const RAW_EGG_COLOR = "#ff6b00";
const COOKIE_PREFERENCES = "eggTimerPreferences";
const COOKIE_TIMER_STATE = "eggTimerState";

const EGG_TIMES = [
  {
    id: "dippy",
    time: 3 * 60,
    name: "DIPPY SOLDIERS",
    description:
      "Perfect for dipping toast soldiers. Runny yolk with barely set whites. Ideal for a luxurious breakfast. 3 minutes for medium eggs.",
    progressPercent: 30,
    color: "#ff8800",
  },
  {
    id: "runny",
    time: 6 * 60,
    name: "RUNNY YOLKS",
    description:
      "Jammy, runny yolks with fully set whites. Great for ramen, rice bowls, or salads. 6 minutes for the perfect jammy egg.",
    progressPercent: 60,
    color: "#f79304",
  },
  {
    id: "soft",
    time: 8 * 60,
    name: "SOFT BOILED",
    description:
      "Creamy, tender yolks that are slightly set. Classic soft-boiled eggs perfect for breakfast or brunch. 8 minutes for medium eggs.",
    progressPercent: 80,
    color: "#ffa600",
  },
  {
    id: "hard",
    time: 10 * 60,
    name: "HARD BOILED",
    description:
      "Fully cooked yolks, perfect for egg salad, sandwiches, or meal prep. 10 minutes for completely set yolks.",
    progressPercent: 100,
    color: "#ffc400",
  },
];

const EggTimeline = ({ selectedIndex, onSelect }) => {
  return (
    <div className="egg-timeline">
      <div className="timeline-line" />
      <div className="timeline-markers">
        {/* Selectable egg markers */}
        {EGG_TIMES.map((egg, index) => (
          <div
            key={egg.id}
            className={`timeline-marker ${
              selectedIndex === index ? "selected" : ""
            }`}
            style={{
              backgroundColor: egg.color,
              color: egg.color,
              left: `${egg.progressPercent}%`,
            }}
            onClick={() => onSelect(index)}
            title={egg.name}
          />
        ))}
      </div>
    </div>
  );
};

const EggTimer = () => {
  const [selectedIndex, setSelectedIndex] = useState(EGG_TIMES.length - 2);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentColor, setCurrentColor] = useState(
    EGG_TIMES[selectedIndex].color
  );
  // eslint-disable-next-line
  const [playBeep, { stop: stopBeep }] = useSound(beepSound, {
    interrupt: true,
    loop: true,
  });
  const [startY, setStartY] = useState(null);
  const selectedType = EGG_TIMES[selectedIndex];

  // Load saved preferences
  useEffect(() => {
    const savedPreferences = Cookies.get(COOKIE_PREFERENCES);
    if (savedPreferences) {
      const { selectedId } = JSON.parse(savedPreferences);
      const savedIndex = EGG_TIMES.findIndex((egg) => egg.id === selectedId);
      if (savedIndex !== -1) {
        setSelectedIndex(savedIndex);
      }
    }

    // Check for active timer
    const timerState = Cookies.get(COOKIE_TIMER_STATE);
    if (timerState) {
      const { endTime, selectedId } = JSON.parse(timerState);
      const now = new Date().getTime();

      if (now < endTime) {
        const savedIndex = EGG_TIMES.findIndex((egg) => egg.id === selectedId);
        if (savedIndex !== -1) {
          setSelectedIndex(savedIndex);
          const remainingTime = Math.ceil((endTime - now) / 1000);
          setTimeLeft(remainingTime);
          setIsActive(true);
        }
      } else {
        // Clear expired timer
        Cookies.remove(COOKIE_TIMER_STATE);
      }
    }
  }, []);

  // Save preferences when selection changes
  useEffect(() => {
    if (!isActive) {
      Cookies.set(
        COOKIE_PREFERENCES,
        JSON.stringify({
          selectedId: selectedType.id,
        }),
        { expires: 365 }
      ); // Store for 1 year
    }
  }, [selectedIndex, isActive, selectedType.id]);

  useEffect(() => {
    if (!isActive) {
      setCurrentColor(selectedType.color);
    } else {
      setCurrentColor(RAW_EGG_COLOR);
    }
  }, [isActive, selectedType.color]);

  useEffect(() => {
    if (isDone) {
      const handleGlobalClick = () => {
        stopBeep();
        setIsDone(false);
        setIsActive(false);
        setProgress(0);
        setCurrentColor(selectedType.color);
      };

      document.addEventListener("click", handleGlobalClick);
      return () => {
        document.removeEventListener("click", handleGlobalClick);
      };
    }
  }, [isDone, selectedType.color, stopBeep]);

  useEffect(() => {
    let interval = null;
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((timeLeft) => {
          const newTime = timeLeft - 1;
          const progress = 1 - newTime / selectedType.time;
          setProgress(progress);

          // Interpolate color from raw to cooked
          const r1 = parseInt(RAW_EGG_COLOR.slice(1, 3), 16);
          const g1 = parseInt(RAW_EGG_COLOR.slice(3, 5), 16);
          const b1 = parseInt(RAW_EGG_COLOR.slice(5, 7), 16);

          const r2 = parseInt(selectedType.color.slice(1, 3), 16);
          const g2 = parseInt(selectedType.color.slice(3, 5), 16);
          const b2 = parseInt(selectedType.color.slice(5, 7), 16);

          const r = Math.round(r1 + (r2 - r1) * progress);
          const g = Math.round(g1 + (g2 - g1) * progress);
          const b = Math.round(b1 + (b2 - b1) * progress);

          setCurrentColor(
            `#${r.toString(16).padStart(2, "0")}${g
              .toString(16)
              .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`
          );

          if (newTime === 0) {
            setIsDone(true);
            playBeep();
          }
          return newTime;
        });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isActive, timeLeft, playBeep, selectedType.time, selectedType.color]);

  const handleTimerClick = () => {
    if (isDone) {
      // eslint-disable-next-line
      stopBeep();
      setIsDone(false);
      setIsActive(false);
      setProgress(0);
      setCurrentColor(selectedType.color);
    }
  };

  const toggleTimer = () => {
    console.log("toggling");
    if (!isActive && !isDone) {
      const duration = selectedType.time;
      const endTime = new Date().getTime() + duration * 1000;

      // Save timer state
      Cookies.set(
        COOKIE_TIMER_STATE,
        JSON.stringify({
          endTime,
          selectedId: selectedType.id,
        })
      );

      setTimeLeft(duration);
      setProgress(0);
      setIsActive(true);
    } else {
      // Stop the timer and beeping
      stopBeep();
      setIsActive(false);
      setIsDone(false);
      setTimeLeft(0);
      setProgress(0);
      Cookies.remove(COOKIE_TIMER_STATE);
    }
  };

  const handleMouseUp = () => {
    // setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (isActive) return;
    setStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (!startY || isActive) return;
    const diff = startY - e.touches[0].clientY;
    if (Math.abs(diff) > 20) {
      setSelectedIndex((prev) => {
        const newIndex =
          diff > 0
            ? (prev + 1) % EGG_TIMES.length
            : (prev - 1 + EGG_TIMES.length) % EGG_TIMES.length;
        setStartY(e.touches[0].clientY);
        return newIndex;
      });
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    return () => {
      stopBeep();
    };
  }, [stopBeep]);

  return (
    <div className="app-container">
      <Navbar />
      <div className={`egg-timer ${isDone ? "timer-done-overlay" : ""}`}>
        {/* Top ad unit */}
        <div className="ad-container top-ad">
          <AdUnit slot="3320920023" /> {/* Replace with your ad slot ID */}
        </div>

        <header className="title-section">
          <h1
            className="title"
            style={{
              borderColor: selectedType.color,
              color: selectedType.color,
            }}
          >
            Egg Timer
          </h1>
          <p className="caption">
            Make your perfect eggs. <br /> Click the yolk to change the texture.{" "}
            <br /> Bring water to a boil first, then gently lower in fridge-cold
            eggs and start the timer.
          </p>
          <Link to="/recipes" className="blog-link">
            View Egg Cooking Recipes →
          </Link>
        </header>

        <section className="timer-container" aria-label="Egg timer controls">
          <svg className="timer-circle" viewBox="0 0 100 100" role="timer">
            <circle
              cx="50"
              cy="50"
              r="45"
              fill={currentColor}
              className="background-circle"
            />
            {isActive && (
              <circle
                cx="50"
                cy="50"
                r="45"
                fill={currentColor}
                strokeDasharray={`${progress} 360`}
                className="timer-progress-mask"
              />
            )}
          </svg>

          <div
            className={`content-container ${isActive ? "timer-active" : ""} ${
              isDone ? "timer-done" : ""
            }`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleMouseUp}
            onClick={handleTimerClick}
          >
            <div className="options-display">
              {isDone ? (
                <div className="option selected">DONE!</div>
              ) : !isActive ? (
                EGG_TIMES.map((egg, index) => (
                  <div
                    key={egg.id}
                    className={`option ${
                      index === selectedIndex ? "selected" : ""
                    }`}
                    style={{
                      transform: `translateY(${
                        (index - selectedIndex) * 40
                      }px)`,
                    }}
                  >
                    {egg.name}
                  </div>
                ))
              ) : (
                <div className="timer-display" role="timer" aria-live="polite">
                  {formatTime(timeLeft)}
                </div>
              )}
            </div>
          </div>
        </section>

        <EggTimeline
          selectedIndex={selectedIndex}
          onSelect={setSelectedIndex}
        />

        <section className="controls">
          <button
            className={`action-button ${isActive ? "reset" : "start"}`}
            onClick={toggleTimer}
            style={
              !isActive
                ? {
                    borderColor: selectedType.color,
                    color: selectedType.color,
                  }
                : {}
            }
            aria-label={isActive ? "Reset timer" : "Start timer"}
          >
            {isActive ? "RESET" : "START TIMER"}
          </button>
        </section>

        <section className="egg-description" aria-live="polite">
          <h2 className="egg-name">{selectedType.name}</h2>
          <p className="egg-detail">{selectedType.description}</p>
        </section>

        <FAQ />

        {/* Bottom ad unit */}
        <div className="ad-container bottom-ad">
          <AdUnit slot="9694756686" /> {/* Replace with your ad slot ID */}
        </div>
      </div>
    </div>
  );
};

export default EggTimer;
